import { applySheets } from "../../../app/utils/styleCache.js";
import { avatarColor, computeInitials } from "../../../app/utils/avatar.js";
import "../../shared/app-loading-dots/app-loading-dots.js";

const sheets = [
    'components/shared/app-input/app-input.css',
    'components/features/app-panel-detail/app-panel-detail.css',
    'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css',
];

export class AppPanelDetail extends HTMLElement {
    config = null;
    currentItem = null;
    currentData = null;
    flashPending = null;
    _cards = [];

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.closeCardMenus = () => {
            this.shadowRoot?.querySelectorAll('.card-ctx-menu.open').forEach(menu => menu.classList.remove('open'));
        };
    }

    connectedCallback() {
        applySheets(this.shadowRoot, sheets);
        document.addEventListener('click', this.closeCardMenus);
    }

    configure(config) {
        if (this.onDataChanged && this.dataChangedEvent) {
            document.removeEventListener(this.dataChangedEvent, this.onDataChanged);
            this.onDataChanged = null;
        }
        this.config = config;
        this.currentItem = null;
        this.currentData = null;
        this.showEmpty();

        if (config.dataChangedEvent) {
            this.dataChangedEvent = config.dataChangedEvent;
            this.onDataChanged = (event) => config.onDataChanged?.(this, event);
            document.addEventListener(config.dataChangedEvent, this.onDataChanged);
        }
    }

    disconnectedCallback() {
        if (this.onDataChanged && this.dataChangedEvent) {
            document.removeEventListener(this.dataChangedEvent, this.onDataChanged);
        }
        document.removeEventListener('click', this.closeCardMenus);
    }

    clear() {
        this.currentItem = null;
        this.currentData = null;
        this.showEmpty();
    }

    async load(item) {
        this.currentItem = item;
        this.shadowRoot.innerHTML = `<div class="loading"><app-loading-dots></app-loading-dots></div>`;
        applySheets(this.shadowRoot, sheets);

        const data = await this.config.loadData(item);
        this.render(data);
    }

    setData(data, item = null) {
        if (item) this.currentItem = item;
        this.render(data);
    }

    async loadWithFlash(item, type = 'create') {
        this.flashPending = type;
        await this.load(item);
    }

    showEmpty() {
        this.shadowRoot.innerHTML = `<div class="empty">${this.config?.emptyText ?? 'Auswählen'}</div>`;
        applySheets(this.shadowRoot, sheets);
    }

    render(data) {
        this.currentData = data;
        this._headerEl = null;
        const config = this.config;
        const name = config.getName(data) ?? '—';
        const avatarSrc = this.currentItem ?? data;
        const avatarStr = (config.getAvatarStr ? config.getAvatarStr(avatarSrc) : null) || name;
        const color = avatarColor(avatarStr);
        const abbr = computeInitials(avatarStr);
        const canEdit = config.canEdit?.() ?? false;
        const canDelete = config.canDelete?.() ?? false;
        const cards = config.renderCards(data);
        this._cards = cards;

        this.shadowRoot.innerHTML = `
            <div class="wrapper">
                <div class="topbar">
                    <div class="topbar-left">
                        <div class="topbar-avatar" style="background:${color}">${abbr}</div>
                        <div class="topbar-info">
                            <div class="topbar-name">${name}</div>
                            <div class="topbar-sub">${config.subLabel ?? ''}</div>
                        </div>
                    </div>
                    <div class="topbar-actions">
                        ${canEdit   ? `<button class="btn-edit"   id="btnEdit"><i class="fa-solid fa-pen-to-square"></i> Bearbeiten</button>` : ''}
                        ${canDelete ? `<button class="btn-delete" id="btnDelete"><i class="fa-solid fa-trash"></i></button>` : ''}
                    </div>
                </div>
                <div class="detail">
                    ${cards.map((c, i) => this.renderCard(c, i)).join('')}
                </div>
            </div>
        `;
        applySheets(this.shadowRoot, sheets);

        if (this.flashPending) {
            const cls = `flash-${this.flashPending}`;
            this.flashPending = null;
            requestAnimationFrame(() => {
                const wrapper = this.shadowRoot.querySelector('.wrapper');
                if (!wrapper) return;
                wrapper.classList.remove('flash-create', 'flash-update');
                void wrapper.offsetWidth;
                wrapper.classList.add(cls);
                setTimeout(() => wrapper.classList.remove(cls), 1900);
            });
        }

        this.shadowRoot.getElementById('btnEdit')?.addEventListener('click', () => config.onEdit?.());
        this.shadowRoot.getElementById('btnDelete')?.addEventListener('click', () => config.onDelete?.());

        this.placeHeader(data);
        this.bindCardEvents();
    }

    updateCards(cards) {
        this._cards = cards;
        const detailEl = this.shadowRoot.querySelector('.detail');
        if (!detailEl) return;
        detailEl.innerHTML = cards.map((c, i) => this.renderCard(c, i)).join('');
        this.placeHeader(this.currentData);
        this.bindCardEvents();
    }

    placeHeader(data) {
        const slot = this.shadowRoot.querySelector('.card-header-slot');
        if (!slot) return;
        if (!this._headerEl) {
            this._headerEl = document.createElement('div');
            this._headerEl.className = 'card-mount card-header-slot';
            this.config.mountHeader(this._headerEl, data, { updateCards: (newCards) => this.updateCards(newCards) });
        }
        slot.replaceWith(this._headerEl);
    }

    bindCardEvents() {
        const cards = this._cards;
        const resolveCard = (cardIdx, subIdx) => {
            const card = cards[cardIdx];
            return subIdx === undefined || Number.isNaN(subIdx) ? card : card?.group?.[subIdx];
        };
        const parseSubIdx = (el) => el.dataset.subIdx !== undefined ? parseInt(el.dataset.subIdx) : undefined;

        this.shadowRoot.querySelectorAll('.card-edit-btn').forEach(btn => {
            const card = resolveCard(parseInt(btn.dataset.cardIdx), parseSubIdx(btn));
            btn.addEventListener('click', event => { event.stopPropagation(); card?.directEdit?.handler?.(); });
        });

        this.shadowRoot.querySelectorAll('.card-ctx-btn').forEach(btn => {
            btn.addEventListener('click', event => {
                event.stopPropagation();
                const menu = btn.nextElementSibling;
                const wasOpen = menu.classList.contains('open');
                this.shadowRoot.querySelectorAll('.card-ctx-menu.open').forEach(m => m.classList.remove('open'));
                if (!wasOpen) menu.classList.add('open');
            });
        });

        this.shadowRoot.querySelectorAll('.card-ctx-item').forEach(btn => {
            btn.addEventListener('click', event => {
                event.stopPropagation();
                const card = resolveCard(parseInt(btn.dataset.cardIdx), parseSubIdx(btn));
                card?.actions?.[parseInt(btn.dataset.actionIdx)]?.handler?.();
                btn.closest('.card-ctx-menu')?.classList.remove('open');
            });
        });

        this.shadowRoot.querySelectorAll('.card.clickable').forEach(el => {
            el.addEventListener('click', (event) => {
                if (event.target.closest('.chip') || event.target.closest('.row')) return;
                cards[parseInt(el.dataset.cardIdx)]?.onClick?.();
            });
        });

        this.shadowRoot.querySelectorAll('.card[data-card-idx]:not(.card-group), .split-section[data-card-idx]').forEach(sectionEl => {
            const card = resolveCard(parseInt(sectionEl.dataset.cardIdx), parseSubIdx(sectionEl));
            sectionEl.querySelectorAll('.chip.clickable').forEach(chipEl => {
                chipEl.addEventListener('click', event => {
                    event.stopPropagation();
                    card?.chips?.[parseInt(chipEl.dataset.chipIdx)]?.onClick?.();
                });
            });
            sectionEl.querySelectorAll('.label.clickable').forEach(labelEl => {
                labelEl.addEventListener('click', event => {
                    event.stopPropagation();
                    card?.rows?.[parseInt(labelEl.dataset.rowIdx)]?.onClick?.();
                });
            });
            sectionEl.querySelectorAll('.value.clickable').forEach(valueEl => {
                valueEl.addEventListener('click', event => {
                    event.stopPropagation();
                    card?.rows?.[parseInt(valueEl.dataset.rowIdx)]?.onValueClick?.();
                });
            });
            sectionEl.querySelectorAll('.row.clickable').forEach(rowEl => {
                rowEl.addEventListener('click', event => {
                    if (event.target.closest('.row-link')) return;
                    const row = card?.rows?.[parseInt(rowEl.dataset.rowIdx)];
                    (row?.onRowClick ?? row?.onClick)?.();
                });
            });
        });
    }

    renderCard(card, idx) {
        if (card.divider) return `<div class="section-divider" data-card-idx="${idx}"></div>`;
        if (card.headerSlot) return `<div class="card-mount card-header-slot"></div>`;
        const wideClass = card.wide ? ' card-wide' : '';
        const warnClass = card.warn ? ' card-warn' : '';
        if (card.group) {
            const sections = card.group.map((sub, subIdx) =>
                `<div class="split-section" data-card-idx="${idx}" data-sub-idx="${subIdx}">${this.renderCardInner(sub, idx, subIdx)}</div>`
            );
            return `<div class="card card-group${wideClass}${warnClass}" data-card-idx="${idx}">${sections.join('<div class="split-divider"></div>')}</div>`;
        }
        return `<div class="card${card.onClick ? ' clickable' : ''}${wideClass}${warnClass}" data-card-idx="${idx}">${this.renderCardInner(card, idx)}</div>`;
    }

    renderCardInner(card, idx, subIdx) {
        const subAttr = subIdx !== undefined ? ` data-sub-idx="${subIdx}"` : '';
        let actionsHtml = '';
        if (card.directEdit) {
            actionsHtml = `<button class="card-edit-btn" data-card-idx="${idx}"${subAttr}><i class="fa-solid fa-pen-to-square"></i></button>`;
        } else if (card.actions?.length) {
            actionsHtml = `
            <div class="card-ctx-wrap">
                <button class="card-ctx-btn"><i class="fa-solid fa-ellipsis-vertical"></i></button>
                <div class="card-ctx-menu">
                    ${card.actions.map((a, i) => `
                        ${a.danger && i > 0 ? '<div class="card-ctx-separator"></div>' : ''}
                        <button class="card-ctx-item${a.danger ? ' danger' : ''}" data-card-idx="${idx}"${subAttr} data-action-idx="${i}">
                            <i class="fa-solid ${a.icon}"></i> ${a.label}
                        </button>
                    `).join('')}
                </div>
            </div>`;
        }

        let content = '';
        if (card.rows) {
            content = card.rows.length
                ? `<div class="rows">${card.rows.map((row, i) => {
                    const rowClickable = row.onRowClick || (card.rowHover && row.onClick);
                    return `<div class="row${rowClickable ? ' clickable' : ''}" data-row-idx="${i}"><span class="label${row.primary ? ' primary' : ''}${row.onClick ? ' clickable' : ''}" data-row-idx="${i}">${row.label}</span><span class="value${row.onValueClick ? ' clickable' : ''}" data-row-idx="${i}">${row.value ?? '—'}</span></div>`;
                  }).join('')}</div>`
                : `<div class="rows"><span class="empty-hint">${card.emptyHint ?? 'Keine Einträge'}</span></div>`;
        } else if (card.stats) {
            content = `<div class="stats">${card.stats.map((s, i) =>
                (i > 0 ? '<div class="stat-divider"></div>' : '') +
                `<div class="stat"><span class="stat-value">${s.value}</span><span class="stat-label">${s.label}</span></div>`
            ).join('')}</div>`;
        } else if (card.chips !== undefined) {
            content = `<div class="chips">${card.chips.length
                ? card.chips.map((chip, i) => `<span class="chip${chip.inactive ? ' inactive' : ''}${chip.onClick ? ' clickable' : ''}" data-chip-idx="${i}">${chip.name}${chip.icon ? ` <i class="fa-solid ${chip.icon}"></i>` : ''}</span>`).join('')
                : `<span class="empty-hint">${card.emptyHint ?? 'Keine Einträge'}</span>`
            }</div>`;
        }

        return `<div class="card-header">
                <div class="card-title">${card.title}</div>
                ${actionsHtml}
            </div>
            ${content}`;
    }
}

customElements.define('app-panel-detail', AppPanelDetail);